import ExpiviIFrameService, {MessageTypes} from '@expivi/iframe-client';
import {analyticsTracker as analytics} from '../analytics/analytics.tracker';

// Expivi fires a price change on every option toggle, so an unthrottled step event
// would flood GA4.
const STEP_THROTTLE_MS = 2000;

const iframe = document.getElementById('expivi-iframe');
const wrapperEl = document.getElementById('expivi-wrapper');

if (iframe) {
    init();
}

function init() {
    const mode = iframe.dataset.mode;
    const gaMode = iframe.dataset.gaMode;
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';
    const service = new ExpiviIFrameService(iframe.contentWindow);

    let inFlight = false;
    const getInFlight = () => inFlight;
    const setInFlight = value => { inFlight = value; };

    fitIframeToViewport(iframe, wrapperEl);

    window.addEventListener('resize', () => fitIframeToViewport(iframe, wrapperEl));

    window.addEventListener('message', e => {
        if (e.data && typeof e.data === 'string') {
            const data = JSON.parse(e.data);
            switch (data.action) {
                case 'RESET':
                case 'RESIZE':
                    iframe.height = data.height;
                    break;
            }
        }
    });

    trackConfiguratorSignals(service, gaMode);

    if (mode === 'dealer') {
        initDealer(service, csrfToken, getInFlight, setInFlight, gaMode);
    } else {
        initConfigurator(service, csrfToken, getInFlight, setInFlight, gaMode);
    }
}

function initConfigurator(service, csrfToken, getInFlight, setInFlight, gaMode) {
    const saveUrl = iframe.dataset.saveUrl;
    const emailUrlTemplate = iframe.dataset.emailUrlTemplate || '';
    const resultEl = document.getElementById('expivi-result');
    const codeEl = document.getElementById('expivi-code');
    const errorEl = document.getElementById('expivi-error');
    const blocksEl = document.getElementById('expivi-blocks');
    const emailFormEl = document.getElementById('expivi-email-form');

    let currentCode = '';
    let currentProductParams = {};

    service.setEventListener(MessageTypes.V1_AddToCart, async () => {
        if (getInFlight()) {
            return;
        }

        setInFlight(true);
        hide(errorEl);

        try {
            const configuration = await service.sendMessage(MessageTypes.V1_SaveCurrentConfiguration);
            const response = await postJson(saveUrl, configuration, csrfToken);

            if (!response.ok) {
                throw new Error(`save endpoint returned ${response.status}`);
            }

            const { code, redirect, ga_product: gaProduct } = await response.json();

            if (!code) {
                throw new Error('save endpoint returned no code');
            }

            // The GA4 item_id/value/currency for this configuration, computed server-side
            // so the quote lead and the configurator lead cannot describe it differently.
            currentProductParams = gaProduct || {};

            analytics.trackConfiguratorComplete(gaMode, {
                ...currentProductParams,
                configuration_code: code,
            });

            if (redirect) {
                window.location.href = redirect;
                return;
            }

            currentCode = code;
            codeEl.textContent = code;
            hide(wrapperEl);
            hide(blocksEl);
            show(resultEl);
            document.body.classList.add('expivi-result-shown');
        } catch (err) {
            console.error('[Expivi]', err);
            show(errorEl);
        } finally {
            setInFlight(false);
        }
    });

    if (emailFormEl) {
        const inputEl = emailFormEl.querySelector('#expivi-email-input');
        const submitEl = emailFormEl.querySelector('button[type="submit"]');
        const messageEl = emailFormEl.querySelector('.expivi-email-form__message');
        const invalidMessage = emailFormEl.dataset.invalidMessage || '';
        const failedMessage = emailFormEl.dataset.failedMessage || 'Error';

        emailFormEl.addEventListener('submit', async event => {
            event.preventDefault();

            if (!currentCode || !emailUrlTemplate || !inputEl) {
                return;
            }

            const email = (inputEl.value || '').trim();
            if (!email || !inputEl.checkValidity()) {
                setMessage(messageEl, invalidMessage || inputEl.validationMessage, 'is-error');
                return;
            }

            submitEl.disabled = true;
            setMessage(messageEl, '', null);

            try {
                const url = emailUrlTemplate.replace('__CODE__', encodeURIComponent(currentCode));
                const response = await postJson(url, { email }, csrfToken);
                const data = await response.json().catch(() => ({}));

                if (response.ok && data.sent) {
                    analytics.trackGenerateLead('configurator', {
                        ...currentProductParams,
                        configuration_code: currentCode,
                    });

                    setMessage(messageEl, data.message || '', 'is-success');
                    inputEl.disabled = true;
                    return;
                }

                if (response.status === 422) {
                    setMessage(messageEl, extractFirstValidationError(data) || invalidMessage, 'is-error');
                    submitEl.disabled = false;
                    return;
                }

                setMessage(messageEl, data.error || data.message || failedMessage, 'is-error');
                submitEl.disabled = response.status === 409;
            } catch (err) {
                console.error('[Expivi][email]', err);
                setMessage(messageEl, failedMessage, 'is-error');
                submitEl.disabled = false;
            }
        });
    }
}

function initDealer(service, csrfToken, getInFlight, setInFlight, gaMode) {
    const orderUrl = iframe.dataset.orderUrl;
    const summaryUrl = iframe.dataset.summaryUrl;
    const failedMessage = iframe.dataset.failedMessage || '';

    const errorEl = document.getElementById('expivi-error');

    iframe.addEventListener('error', () => showError(errorEl, failedMessage));

    service.setEventListener(MessageTypes.V1_AddToCart, async () => {
        if (getInFlight()) {
            return;
        }

        setInFlight(true);
        hide(errorEl);

        try {
            const configuration = await service.sendMessage(MessageTypes.V1_SaveCurrentConfiguration, [512, 512]);
            const response = await postJson(orderUrl, configuration, csrfToken);

            if (!response.ok) {
                throw new Error(`order endpoint returned ${response.status}`);
            }

            const data = await response.json();

            analytics.trackConfiguratorComplete(gaMode, data?.ga_product || {});

            window.location.href = data?.redirect || summaryUrl;
        } catch (err) {
            console.error('[Expivi][dealer]', err);
            showError(errorEl, failedMessage);
        } finally {
            setInFlight(false);
        }
    });
}

function trackConfiguratorSignals(service, gaMode) {
    let started = false;
    let steps = 0;
    let lastStepAt = 0;

    // Any Expivi signal means the configurator is live -- v1.ready is not guaranteed to
    // reach the parent frame, so it cannot be the only start trigger.
    service.setEventListener('*', message => {
        if (!started) {
            started = true;
            analytics.trackConfiguratorStart(gaMode);
        }

        if (message.type !== MessageTypes.V1_PriceChanged) {
            return;
        }

        const now = Date.now();

        if (now - lastStepAt < STEP_THROTTLE_MS) {
            return;
        }

        lastStepAt = now;
        steps++;

        analytics.trackConfiguratorStep(gaMode, steps);
    });
}

function fitIframeToViewport(iframeEl, wrapperEl) {
    const top = iframeEl.getBoundingClientRect().top + window.scrollY;
    const wrapperStyles = wrapperEl ? getComputedStyle(wrapperEl) : null;
    const bottomMargin = wrapperStyles ? parseFloat(wrapperStyles.marginBottom) || 0 : 0;
    const available = window.innerHeight - top - bottomMargin;

    iframeEl.height = `${Math.max(available, 480)}`;
}

function postJson(url, body, csrfToken) {
    return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify(body),
    });
}

function extractFirstValidationError(data) {
    const firstField = Object.values(data?.errors || {})[0];
    return (Array.isArray(firstField) && firstField[0]) || data?.message || '';
}

function setMessage(el, text, state) {
    if (!el) {
        return;
    }
    el.textContent = text || '';
    el.classList.remove('is-success', 'is-error');
    if (state) {
        el.classList.add(state);
    }
}

function showError(el, message) {
    if (!el) {
        return;
    }
    if (message) {
        el.textContent = message;
    }
    el.style.display = '';
}

function show(el) {
    if (el) {
        el.style.display = '';
    }
}

function hide(el) {
    if (el) {
        el.style.display = 'none';
    }
}
